import { useTheme } from "@emotion/react";
import { AppLinkTree } from "@megarax/links";
import { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom-v5-compat";

import { SignalsBadge } from "../signals/SignalsBadge";
import { useSignals } from "../signals/SignalsProvider";
import { isSignalUnread } from "../signals/signalUnread";
import { navAccent } from "./navConstants";
import { isAppTreeActive, isLinkExternal, isPageLinkActive, linkHref } from "./navHelpers";
import { NavLinkRow } from "./NavLinkRow";

type Props = {
  appTree: AppLinkTree;
  onNavigate?: () => void;
};

export const MegaraxNavSection: React.FC<Props> = ({ appTree, onNavigate }) => {
  const theme = useTheme();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { signals, markRead } = useSignals();
  const isActiveApp = isAppTreeActive(appTree, location.pathname, searchParams);

  const activeBadgeTypes = appTree.pageLinks.find(
    (pageLink) => pageLink.badgeSignalTypes && isPageLinkActive(pageLink, location.pathname, searchParams),
  )?.badgeSignalTypes;

  useEffect(() => {
    if (!activeBadgeTypes) return;

    const badgeTypes = new Set<string>(activeBadgeTypes);
    markRead(
      signals
        .filter((signal) => badgeTypes.has(signal.signalType) && isSignalUnread(signal))
        .map((signal) => signal.docId),
    );
  }, [activeBadgeTypes, signals, markRead]);

  if (appTree.pageLinks.length === 0) return null;

  const sectionColor = isActiveApp ? appTree.color : navAccent;

  return (
    <div css={{ display: "flex", flexDirection: "column" }}>
      <div
        css={{
          display: "flex",
          alignItems: "center",
          gap: "0.625rem",
          padding: "0.375rem 0.25rem",
          borderRadius: theme.smallBorderRadius,
          color: sectionColor,
          fontFamily: theme.displayFontFamily,
          fontWeight: isActiveApp ? 700 : 500,
          fontSize: "0.9375rem",
          textAlign: "left",
          width: "100%",
        }}
      >
        <appTree.Icon css={{ width: "1.125rem", height: "1.125rem", flexShrink: 0 }} />
        <span css={{ flex: 1 }}>{appTree.name}</span>
      </div>

      <div
        css={{
          display: "flex",
          flexDirection: "column",
          gap: "0.125rem",
          marginLeft: "0.625rem",
          paddingLeft: "0.875rem",
          marginTop: "0.25rem",
          marginBottom: "0.375rem",
          borderLeft: `2px solid ${isActiveApp ? appTree.color : theme.colors.skeleton}`,
        }}
      >
        {appTree.pageLinks.map((pageLink) => (
          <NavLinkRow
            key={pageLink.name}
            to={linkHref(pageLink.uri)}
            isExternal={isLinkExternal(pageLink.uri)}
            isActive={isPageLinkActive(pageLink, location.pathname, searchParams)}
            accentColor={isActiveApp ? appTree.color : undefined}
            icon={<pageLink.Icon css={{ width: "0.875rem", height: "0.875rem" }} />}
            onClick={onNavigate}
            trailing={
              pageLink.badgeSignalTypes ? (
                <SignalsBadge signals={signals} badgeSignalTypes={pageLink.badgeSignalTypes} color={appTree.color} />
              ) : undefined
            }
          >
            {pageLink.name}
          </NavLinkRow>
        ))}
      </div>
    </div>
  );
};
